import { RatioQuestion, RatioNotation, RatioType, RatioContext } from './RatioQuestionsColored';

// =============================================================================
// TYPES FOR TRACKING STUDENT PROGRESS
// =============================================================================

/**
 * Problem categories we track for mastery
 */
export type ProblemCategory =
  | 'part-to-part'
  | 'part-to-whole'
  | 'implicit-total'
  | 'explicit-total'
  | 'reversed-order'
  | 'colon'
  | 'fraction'
  | 'word-form';

/**
 * Record of whether student has demonstrated each category
 * and whether they got it right on first try
 */
export interface CategoryDemonstration {
  demonstrated: boolean;
  firstTry: boolean;
}

/**
 * Tracking for student's question history
 */
export interface QuestionHistory {
  // Map of category name -> demonstration status
  categories: Record<ProblemCategory, CategoryDemonstration>;

  // Set of question IDs the student has seen
  seenQuestionIds: Set<string>;

  // Set of context IDs (examples) the student has seen
  seenContextIds: Set<string>;

  // Set of example categories the student has seen
  seenExampleCategories: Set<string>;

  // Most recent question (for similarity matching after wrong answers)
  lastQuestion: RatioQuestion | null;

  // Whether the last answer was correct on first try
  lastAnswerCorrectFirstTry: boolean;
}

const PROBLEM_CATEGORIES: ProblemCategory[] = [
  'part-to-part',
  'part-to-whole',
  'implicit-total',
  'explicit-total',
  'reversed-order',
  'colon',
  'fraction',
  'word-form',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const toStringSet = (value: unknown, fallback: Set<string>): Set<string> => {
  if (value instanceof Set) {
    return new Set(Array.from(value).filter((entry): entry is string => typeof entry === 'string'));
  }
  if (Array.isArray(value)) {
    return new Set(value.filter((entry): entry is string => typeof entry === 'string'));
  }
  return new Set(fallback);
};

// =============================================================================
// CATEGORY EXTRACTION
// =============================================================================

/**
 * Extract all problem categories that apply to a given question
 */
export function extractCategories(question: RatioQuestion): ProblemCategory[] {
  const categories: ProblemCategory[] = [];

  // Type categories
  if (question.type === 'part-to-part') {
    categories.push('part-to-part');
  } else if (question.type === 'part-to-whole') {
    categories.push('part-to-whole');

    // Total explicitness only applies to part-to-whole
    if (question.explicitTotal) {
      categories.push('explicit-total');
    } else {
      categories.push('implicit-total');
    }
  }

  // Order category
  if (question.reverse) {
    categories.push('reversed-order');
  }

  // Notation categories
  if (question.notation === 'colon') {
    categories.push('colon');
  } else if (question.notation === 'fraction') {
    categories.push('fraction');
  } else if (question.notation === 'word') {
    categories.push('word-form');
  }

  return categories;
}

/**
 * Get the "structure" of a question for similarity matching.
 * Two questions have the same structure if they match on these dimensions.
 */
export interface QuestionStructure {
  type: RatioType;
  reversed: boolean;
  explicitTotal: boolean; // only relevant for part-to-whole
  notation: RatioNotation;
}

export function extractStructure(question: RatioQuestion): QuestionStructure {
  return {
    type: question.type,
    reversed: question.reverse,
    explicitTotal: question.explicitTotal,
    notation: question.notation,
  };
}

export function structuresMatch(s1: QuestionStructure, s2: QuestionStructure): boolean {
  if (s1.type !== s2.type) return false;
  if (s1.reversed !== s2.reversed) return false;
  if (s1.notation !== s2.notation) return false;

  // Only check explicitTotal for part-to-whole questions
  if (s1.type === 'part-to-whole' && s1.explicitTotal !== s2.explicitTotal) {
    return false;
  }

  return true;
}

// =============================================================================
// HISTORY INITIALIZATION
// =============================================================================

export function createInitialHistory(): QuestionHistory {
  return {
    categories: {
      'part-to-part': { demonstrated: false, firstTry: false },
      'part-to-whole': { demonstrated: false, firstTry: false },
      'implicit-total': { demonstrated: false, firstTry: false },
      'explicit-total': { demonstrated: false, firstTry: false },
      'reversed-order': { demonstrated: false, firstTry: false },
      colon: { demonstrated: false, firstTry: false },
      fraction: { demonstrated: false, firstTry: false },
      'word-form': { demonstrated: false, firstTry: false },
    },
    seenQuestionIds: new Set(),
    seenContextIds: new Set(),
    seenExampleCategories: new Set(),
    lastQuestion: null,
    lastAnswerCorrectFirstTry: false,
  };
}

export function normalizeQuestionHistory(
  raw: unknown,
  fallback: QuestionHistory = createInitialHistory(),
): QuestionHistory {
  if (!isRecord(raw)) {
    return fallback;
  }

  const rawCategories = isRecord(raw.categories) ? raw.categories : {};
  const normalizedCategories = { ...fallback.categories };

  for (const category of PROBLEM_CATEGORIES) {
    const value = rawCategories[category];
    if (!isRecord(value)) {
      continue;
    }

    normalizedCategories[category] = {
      demonstrated:
        typeof value.demonstrated === 'boolean' ? value.demonstrated : fallback.categories[category].demonstrated,
      firstTry: typeof value.firstTry === 'boolean' ? value.firstTry : fallback.categories[category].firstTry,
    };
  }

  return {
    categories: normalizedCategories,
    seenQuestionIds: toStringSet(raw.seenQuestionIds, fallback.seenQuestionIds),
    seenContextIds: toStringSet(raw.seenContextIds, fallback.seenContextIds),
    seenExampleCategories: toStringSet(raw.seenExampleCategories, fallback.seenExampleCategories),
    lastQuestion: isRecord(raw.lastQuestion) ? (raw.lastQuestion as unknown as RatioQuestion) : fallback.lastQuestion,
    lastAnswerCorrectFirstTry:
      typeof raw.lastAnswerCorrectFirstTry === 'boolean'
        ? raw.lastAnswerCorrectFirstTry
        : fallback.lastAnswerCorrectFirstTry,
  };
}

// =============================================================================
// HISTORY UPDATES
// =============================================================================

/**
 * Update history after a student answers a question correctly
 */
export function recordCorrectAnswer(
  history: QuestionHistory,
  question: RatioQuestion,
  contextCategory: string,
  isFirstTry: boolean,
): QuestionHistory {
  const categories = extractCategories(question);

  // Update category demonstrations
  const updatedCategories = { ...history.categories };
  for (const cat of categories) {
    updatedCategories[cat] = {
      demonstrated: true,
      firstTry: updatedCategories[cat].firstTry || isFirstTry, // Once true, stays true
    };
  }

  return {
    ...history,
    categories: updatedCategories,
    seenQuestionIds: new Set([...history.seenQuestionIds, question.id]),
    seenContextIds: new Set([...history.seenContextIds, question.contextId]),
    seenExampleCategories: new Set([...history.seenExampleCategories, contextCategory]),
    lastQuestion: question,
    lastAnswerCorrectFirstTry: isFirstTry,
  };
}

/**
 * Update history after a student answers incorrectly
 */
export function recordIncorrectAnswer(history: QuestionHistory, question: RatioQuestion): QuestionHistory {
  return {
    ...history,
    lastQuestion: question,
    lastAnswerCorrectFirstTry: false,
  };
}

// =============================================================================
// QUESTION SELECTION
// =============================================================================

/**
 * Select the next question based on student history and question pool.
 *
 * Rules:
 * 1. If last answer was wrong, give a question with the same structure
 * 2. If last answer was correct on first try, prioritize questions with new categories
 * 3. Apply filters in order of preference (only if filtered set is non-empty):
 *    - Not seen before
 *    - Not in a seen category
 *    - Not from a seen example
 */
export function selectNextQuestion(
  allQuestions: RatioQuestion[],
  allContexts: RatioContext[],
  history: QuestionHistory,
): RatioQuestion | null {
  if (allQuestions.length === 0) return null;

  // Create a context lookup map
  const contextMap = new Map(allContexts.map(ctx => [ctx.id, ctx]));

  let candidates = [...allQuestions];

  // STRATEGY 1: If last answer was wrong, find questions with same structure
  if (history.lastQuestion && !history.lastAnswerCorrectFirstTry) {
    const targetStructure = extractStructure(history.lastQuestion);
    const sameStructure = candidates.filter(q => structuresMatch(extractStructure(q), targetStructure));

    if (sameStructure.length > 0) {
      candidates = sameStructure;
    }
  }

  // STRATEGY 2: If last answer was correct on first try, prioritize new categories
  if (history.lastQuestion && history.lastAnswerCorrectFirstTry) {
    const withNewCategories = candidates.filter(q => {
      const qCategories = extractCategories(q);
      // Check if any category hasn't been demonstrated yet
      return qCategories.some(cat => !history.categories[cat].demonstrated);
    });

    if (withNewCategories.length > 0) {
      candidates = withNewCategories;
    }
  }

  // FILTER 1: Prefer unseen questions
  const unseen = candidates.filter(q => !history.seenQuestionIds.has(q.id));
  if (unseen.length > 0) {
    candidates = unseen;
  }

  // FILTER 2: Prefer questions from unseen example categories
  const unseenExampleCategory = candidates.filter(q => {
    const ctx = contextMap.get(q.contextId);
    return ctx && !history.seenExampleCategories.has(ctx.category);
  });
  if (unseenExampleCategory.length > 0) {
    candidates = unseenExampleCategory;
  }

  // FILTER 3: Prefer questions from unseen contexts
  const unseenContext = candidates.filter(q => !history.seenContextIds.has(q.contextId));
  if (unseenContext.length > 0) {
    candidates = unseenContext;
  }

  // Select randomly from remaining candidates
  if (candidates.length === 0) return null;

  const randomIndex = Math.floor(Math.random() * candidates.length);
  return candidates[randomIndex];
}

// =============================================================================
// MASTERY CHECKING
// =============================================================================

/**
 * Check if student has demonstrated all categories
 */
export function hasCompletedAllCategories(history: QuestionHistory): boolean {
  return Object.values(history.categories).every(cat => cat.demonstrated);
}

/**
 * Check if student has demonstrated all categories on first try
 */
export function hasCompletedAllCategoriesFirstTry(history: QuestionHistory): boolean {
  return Object.values(history.categories).every(cat => cat.firstTry);
}

/**
 * Get categories that haven't been demonstrated yet
 */
export function getRemainingCategories(history: QuestionHistory): ProblemCategory[] {
  return (Object.keys(history.categories) as ProblemCategory[]).filter(cat => !history.categories[cat].demonstrated);
}

/**
 * Get categories demonstrated but not on first try
 */
export function getCategoriesNeedingFirstTry(history: QuestionHistory): ProblemCategory[] {
  return (Object.keys(history.categories) as ProblemCategory[]).filter(
    cat => history.categories[cat].demonstrated && !history.categories[cat].firstTry,
  );
}
